import React from 'react'

import { Box, Button, useToggle } from '@factorialco/deprecated-design-system'
import { BudgetFragment, UpdateFinanceBudgetMutationVariables } from 'generated/resources/graphql'
import moment from 'moment'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useForm } from 'modules/core/hooks/useForm'
import { track } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import ConfirmationModal from 'modules/finance/components/Budgets/Detail/ConfirmationModal'
import {
  useArchiveBudget,
  useUnarchiveBudget,
  useUpdateBudget,
} from 'modules/finance/hooks/useBudgets'
import { events } from 'modules/finance/lib/events'

import EditBudgetForm from './Form'

interface EditBudgetSidepanelProps {
  budget: BudgetFragment
  openEditBudgetSidePanel: boolean
  onClose: () => void
  onSubmit: () => void
}

const EditBudgetSidepanel = ({
  openEditBudgetSidePanel,
  onClose,
  budget,
  onSubmit,
}: EditBudgetSidepanelProps) => {
  const [openUnarchiveBudget, toggleOpenUnarchiveBudget] = useToggle(false)
  const [openArchiveBudget, toggleOpenArchiveBudget] = useToggle(false)
  const [archiving, setArchiving] = React.useState(false)

  useEffect(() => {
    track(events.openBudgetSidepanel, { budgetId: budget.id })
  }, [])

  const { archiveBudget } = useArchiveBudget()

  const { unarchiveBudget } = useUnarchiveBudget()

  const { updateBudget } = useUpdateBudget()

  const handleArchive = async () => {
    const result = await archiveBudget({ id: budget.id })

    const handled = handleResult(result, (_) => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('budgets.forms.edit_budget.flash.budget_archive_ok'),
      })

      onSubmit()
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('budgets.forms.edit_budget.flash.budget_archive_error'),
      })
    }
  }

  const handleUnarchive = async () => {
    const result = await unarchiveBudget({ id: budget.id })

    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('budgets.forms.edit_budget.flash.budget_unarchive_ok'),
      })

      onSubmit()
      toggleOpenUnarchiveBudget(false)()
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('budgets.forms.edit_budget.flash.budget_unarchive_error'),
      })
    }
  }

  const form = useForm<UpdateFinanceBudgetMutationVariables>({
    defaultValues: {
      id: budget.id,
      legalEntityId: budget.legalEntityId,
      name: budget.name,
      description: budget?.description || '',
      ownerEmployeeIds: budget.ownerEmployees.map((ownerEmployee) => ownerEmployee.id),
      viewerEmployeeIds: budget.viewerEmployees.map((viewerEmployee) => viewerEmployee.id),
      effectiveFrom: budget.effectiveFrom,
      effectiveUntil: budget.effectiveUntil,
      periodicity: budget.periodicity,
      amountCents: budget.amountCents,
    },
    onSubmit: async (data) => {
      const result = await updateBudget({
        id: budget.id,
        legalEntityId: data.legalEntityId,
        name: data.name,
        description: data.description,
        amountCents: data.amountCents,
        effectiveFrom: data.effectiveFrom,
        effectiveUntil: data.effectiveUntil,
        ownerEmployeeIds: data.ownerEmployeeIds,
        viewerEmployeeIds: data.viewerEmployeeIds,
      })

      if (
        form.values.effectiveUntil &&
        moment(form.values.effectiveFrom).isAfter(moment(form.values.effectiveUntil))
      ) {
        form.setErrors({ ...form.errors, effectiveFrom: i18n.t('budgets.forms.errors.start_date') })
      }

      if (Number(form?.values?.amountCents) <= 0) {
        form.setErrors({
          ...form.errors,
          amountCents: i18n.t('budgets.forms.errors.invalid_amount'),
        })
      }

      const handled = handleResult(result, (formErrors) => {
        form.setErrors({
          name: formErrors.name,
          legalEntityId: formErrors.legalEntityId,
          description: formErrors.description,
          amountCents: formErrors.amountCents,
          effectiveFrom: formErrors.effectiveFrom,
          effectiveUntil: formErrors.effectiveUntil,
          periodicity: formErrors.periodicity,
          ownerEmployeeIds: formErrors.ownerEmployeeIds,
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('budgets.forms.edit_budget.flash.budget_updated_ok'),
        })
        onSubmit()
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('budgets.forms.edit_budget.flash.budget_updated_error'),
        })
      }

      return handled
    },
  })

  return (
    <>
      {openUnarchiveBudget && (
        <ConfirmationModal
          title={i18n.t('budgets.detail.unarchive_modal.title')}
          description={i18n.t('budgets.detail.unarchive_modal.description')}
          handleSubmit={handleUnarchive}
          closeModal={toggleOpenUnarchiveBudget(false)}
          submitText={i18n.t('common.unarchive')}
        />
      )}

      {openArchiveBudget && (
        <ConfirmationModal
          title={i18n.t('budgets.detail.archive_modal.title')}
          description={i18n.t('budgets.detail.archive_modal.description')}
          handleSubmit={handleArchive}
          closeModal={toggleOpenArchiveBudget(false)}
          submitText={i18n.t('common.archive')}
        />
      )}
      <Sidepanel
        open={openEditBudgetSidePanel}
        onClose={onClose}
        width="wide"
        title={i18n.t('budgets.forms.edit_budget.title')}
        footer={
          budget._policies?.update.can && (
            <Box flexDirection="rowReverse" gap="s12">
              <Button
                onClick={() => {
                  if (!budget.archived) {
                    if (archiving) {
                      toggleOpenArchiveBudget(true)()
                    } else {
                      form.handleSubmit()
                    }
                  } else {
                    toggleOpenUnarchiveBudget(true)()
                  }
                }}
                disabled={form.isSubmitting}
              >
                {i18n.t('common.save')}
              </Button>
              <Button onClick={onClose} type="secondary">
                {i18n.t('common.cancel')}
              </Button>
            </Box>
          )
        }
      >
        <EditBudgetForm form={form} budget={budget} setArchiving={setArchiving} />
      </Sidepanel>
    </>
  )
}

export default EditBudgetSidepanel
